import React, { useEffect, useState } from 'react';
import './reports.css';
import './reports-beautiful.css';
import useService from 'views/default/useService';
import { useGetAllCommonStudentInitialDataMutation, usePostCommonMutation } from 'slices/get/common';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { Card, Button } from 'react-bootstrap';
import { PageHeader, LoadingSpinner, NoDataFound } from './CommonComponents';
import './common-components.css';

const ProgressReport = () => {
  const { checkAccessTime, fetchAccessToken } = useService();
  const [getAllCommonStudentInitialData] = useGetAllCommonStudentInitialDataMutation();
  const location = useLocation();
  const currentPath = location.pathname;
  const history = useHistory();
  const [postCommon] = usePostCommonMutation();
  const [anualReportData, setAnualReportData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const access_token = fetchAccessToken();

    postCommon({
      ep: `assessment/reports_multiple`,
      token: access_token
    }).then((response) => {
      if (response && response?.data && response.data.status) {
        setAnualReportData(response.data.data);
      }
      setLoading(false);
      checkAccessTime(response, currentPath);
    }).catch(() => {
      setLoading(false);
    });
  }, [currentPath]);

  // Show loading state
  if (loading) {
    return (
      <div className="report-page-beautiful">
        <LoadingSpinner message="Loading annual report..." fullPage={true} />
      </div>
    );
  }

  // Show no data state
  if (!anualReportData) {
    return (
      <div className="report-page-beautiful">
        <NoDataFound
          title="Report Not Available"
          message="Your annual progress report is not available yet. Please check back later."
          icon="file-earmark-x"
          showBackButton={true}
        />
      </div>
    );
  }

  const { school, address, name, standard, section, subjects, gradingScale } = anualReportData;

  // Get grade color
  const getGradeColor = (grade) => {
    if (!grade) return '#888';
    if (grade.startsWith('A')) return '#11998e';
    if (grade.startsWith('B')) return '#667eea';
    if (grade.startsWith('C')) return '#f7971e';
    return '#ff416c';
  };

  return (
    <div className="report-page-beautiful">
      {/* Beautiful Header with Back Button */}
      <PageHeader
        title="Annual Progress Report"
        subtitle={`Academic Year Report for ${name || 'Student'}`}
        icon="journal-bookmark"
        showBackButton={true}
        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      />

      {/* Report Card */}
      <Card className="report-card-container">
        {/* School Header */}
        <div className="school-header-beautiful">
          <div className="school-logo-placeholder">
            <i className="bi bi-mortarboard-fill"></i>
          </div>
          <h1 className="school-name-beautiful">{school}</h1>
          <p className="school-address-beautiful">{address || "Address not provided"}</p>
          <div className="report-title-badge">
            <i className="bi bi-star-fill me-2"></i>
            Annual Progress Report
          </div>
        </div>

        {/* Student Info */}
        <div className="student-info-beautiful">
          <div className="student-details-beautiful">
            <div className="detail-item">
              <span className="detail-label">Student Name</span>
              <span className="detail-value">{name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Standard</span>
              <span className="detail-value">{standard}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Section</span>
              <span className="detail-value">{section}</span>
            </div>
          </div>
          <div className="student-photo-beautiful">
            <div className="photo-placeholder">
              <i className="bi bi-person-fill"></i>
            </div>
          </div>
        </div>

        {/* Marks Table */}
        <div className="table-wrapper-beautiful">
          <table className="report-table-beautiful">
            <thead>
              <tr>
                <th>Subject</th>
                <th>FA-1</th>
                <th>FA-2</th>
                <th>FA-3</th>
                <th>FA-4</th>
                <th>SA-1</th>
                <th>SA-2</th>
                <th>SA-3</th>
                <th>Grade</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {subjects && subjects.map((subject, index) => (
                <tr key={index} style={{ animationDelay: `${index * 0.05}s` }}>
                  <td className="subject-name-cell">{subject.name}</td>
                  <td>{subject["FA-1"] || "-"}</td>
                  <td>{subject["FA-2"] || "-"}</td>
                  <td>{subject["FA-3"] || "-"}</td>
                  <td>{subject["FA-4"] || "-"}</td>
                  <td>{subject["SA-1"] || "-"}</td>
                  <td>{subject["SA-2"] || "-"}</td>
                  <td>{subject["SA-3"] || "-"}</td>
                  <td>
                    <span className="grade-badge" style={{ background: getGradeColor(subject.grade) }}>
                      {subject.grade || "N/A"}
                    </span>
                  </td>
                  <td className="remarks-cell">{subject.overall_remarks || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Grading Scale */}
        {gradingScale && gradingScale.length > 0 && (
          <div className="grading-scale-beautiful">
            <h3 className="grading-title">
              <i className="bi bi-info-circle me-2"></i>
              Grading Scale
            </h3>
            <div className="grading-grid">
              {gradingScale.map((scale, index) => (
                <div key={index} className="grading-item" style={{ animationDelay: `${index * 0.05}s` }}>
                  <div className="grading-range">{scale.range}</div>
                  <div className="grading-grade" style={{ background: getGradeColor(scale.grade) }}>{scale.grade}</div>
                  <div className="grading-remarks">{scale.remarks}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signatures */}
        <div className="signatures-beautiful">
          <div className="signature-item">
            <div className="signature-line"></div>
            <p>Teacher's Signature</p>
          </div>
          <div className="signature-item">
            <div className="signature-line"></div>
            <p>Principal's Signature</p>
          </div>
          <div className="signature-item">
            <div className="signature-line"></div>
            <p>Parent's Signature</p>
          </div>
        </div>

        {/* Print Button */}
        <div className="report-actions">
          <Button className="print-button-beautiful" onClick={() => window.print()}>
            <i className="bi bi-printer me-2"></i>
            Print Report
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ProgressReport;
